import { replace, type LoaderFunction } from 'react-router'
import { firstValueFrom, takeUntil } from 'rxjs'
import { fromAbortSignal } from '@/utils/rx-util'
import { Id } from '@/infra/id'
import { createUrlWithParams } from '@/utils/url-util'
import type { CreateBookkeepingRoutesOptions } from './routes'

/**
 * Creates a route loader that creates a {@link XeroAccount} when sufficient query
 * parameters have been supplied.
 */
export function createXeroOAuth2Loader(
  options: CreateBookkeepingRoutesOptions,
): LoaderFunction {
  return async ({ request }) => {
    const store = await options.bookkeepingPlatformConnectionStore
    const searchParams = new URL(request.url).searchParams

    const code = searchParams.get('code')
    const key = searchParams.get('state')

    if (!code || !key) {
      throw new Error('Missing required parameters')
    }

    const state = store.getXeroSecurityState(key)
    if (!state) {
      throw new Error('The state is invalid')
    }

    const localReturnUrl = state.localReturnUrl
    if (!localReturnUrl) {
      throw new Error('Missing local return URL')
    }

    const signal$ = fromAbortSignal(request.signal)
    const workspaceId = await firstValueFrom(
      options.currentWorkspaceId$.pipe(takeUntil(signal$)),
      { defaultValue: null },
    )

    if (!workspaceId) {
      throw new Error('The workspace ID not found')
    }

    try {
      const account = await store
        .createPlatformConnectionFromOAuth2Code({
          type: 'Xero',
          workspaceId,
          code,
        })
        .catch(
          options.createSnackbarErrorHandler('Unable to connect with Xero'),
        )

      const url = createUrlWithParams(localReturnUrl, {
        connection_id: Id.assert(account.connectionId),
        bookkeeping_platform: 'xero',
      })

      return replace(url.toString())
    } catch (error: unknown) {
      console.log(error)
      return replace(localReturnUrl)
    }
  }
}
